import { c as _c } from "react/compiler-runtime";
import { Badge, Box, Text, chakra, Wrap, WrapItem } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { ScoCommentThinking } from '@src/graphql-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LuSparkles } from 'react-icons/lu';
type ChatThinkingPanelProps = {
  thinking?: ScoCommentThinking | null;
  compact?: boolean;
  /** True once the assistant answer body has started streaming. */
  hasAnswerBody?: boolean;
  /**
   * When true, show thinking even after body started / turn completed
   * (user opened via brain hover action).
   */
  forceVisible?: boolean;
};
const badgePopIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.72) translateY(4px);
  }
  60% {
    opacity: 0.5;
    transform: scale(1.06) translateY(0);
  }
  100% {
    opacity: 0.5;
    transform: scale(1) translateY(0);
  }
`;

/** True when this comment has thinking worth showing (live stream or stored text). */
export function hasCommentThinking(thinking?: ScoCommentThinking | null): boolean {
  if (!thinking) {
    return false;
  }
  if (thinking.status === 'unavailable' || thinking.status === 'idle') {
    return false;
  }
  if (thinking.status === 'streaming') {
    return true;
  }
  return !!(thinking.text && thinking.text.trim());
}

/**
 * Pull headline-like phrases from thinking markdown:
 * - bold spans: **title** / __title__
 * - ATX headings: # Title
 * Deduped in first-seen order; nested markdown stripped for badge labels.
 */
export function extractThinkingHeadlines(markdown: string): string[] {
  if (!markdown) {
    return [];
  }
  const found: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string) => {
    const cleaned = raw.replace(/[*_~`]+/g, '').replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    if (!cleaned || cleaned.length < 2) {
      return;
    }
    const key = cleaned.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    found.push(cleaned);
  };

  // Bold segments (non-greedy, no newlines inside — line-oriented “headlines”)
  const boldRe = /(\*\*|__)([^*\n_]+?)\1/g;
  let m: RegExpExecArray | null;
  while ((m = boldRe.exec(markdown)) !== null) {
    push(m[2]);
  }

  // Markdown headings
  for (const line of markdown.split('\n')) {
    const heading = line.match(/^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/);
    if (heading) {
      push(heading[1]);
    }
  }
  return found;
}

/**
 * Thinking / chain-of-thought for AI chat messages.
 *
 * - Live phase (thinking streaming, no answer body yet): wrapping row
 *   "Thinking: (badge) (badge) … View all"
 * - Once answer body starts: hidden unless `forceVisible` (brain hover action)
 * - "View all" expands full markdown below the row
 */
export const ChatThinkingPanel = props => {
  const $ = _c(42);
  const {
    thinking,
    compact,
    hasAnswerBody: t0,
    forceVisible: t1
  } = props;
  const hasAnswerBody = t0 === undefined ? false : t0;
  const forceVisible = t1 === undefined ? false : t1;
  const status = thinking?.status || "";
  const t2 = thinking?.text || "";
  let t3;
  if ($[0] !== t2) {
    t3 = t2.trim();
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const text = t3;
  const isStreaming = status === "streaming";
  const isError = status === "error";
  const fullScrollRef = useRef(null);
  let t4;
  if ($[2] !== text) {
    t4 = extractThinkingHeadlines(text);
    $[2] = text;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const headlines = t4;
  const hasContent = hasCommentThinking(thinking);
  const isLiveThinking = isStreaming && !hasAnswerBody;
  const shouldShow = hasContent && (isLiveThinking || forceVisible);
  const [showFullMarkdown, setShowFullMarkdown] = useState(false);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setShowFullMarkdown(false);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = thinking?.startedAt;
  let t7;
  if ($[5] !== t6) {
    t7 = [t6];
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  useEffect(t5, t7);
  let t8;
  if ($[7] !== showFullMarkdown) {
    t8 = () => {
      if (!showFullMarkdown || !fullScrollRef.current) {
        return;
      }
      const el = fullScrollRef.current;
      el.scrollTop = el.scrollHeight;
    };
    $[7] = showFullMarkdown;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== showFullMarkdown || $[10] !== text) {
    t9 = [text, showFullMarkdown];
    $[9] = showFullMarkdown;
    $[10] = text;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  useEffect(t8, t9);
  if (!shouldShow) {
    return null;
  }
  const label = isStreaming ? "Thinking:" : isError ? "Thinking (error)" : "Thinking:";
  const displayText = text || (isStreaming ? "..." : isError ? "Thinking failed." : "");
  const canViewAll = !!text && !isError;
  const t10 = compact ? 1 : 2;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Box as={LuSparkles} flexShrink={0} fontSize="0.85em" />;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const t12 = compact ? "xs" : "xs";
  let t13;
  if ($[13] !== label || $[14] !== t12) {
    t13 = <Text fontFamily="Poppins" fontSize={t12}>{label}</Text>;
    $[13] = label;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== thinking?.truncated) {
    t14 = thinking?.truncated ? <Text color="colorPalette.fg/40" fontSize="xs">(truncated)</Text> : null;
    $[16] = thinking?.truncated;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t13 || $[19] !== t14) {
    t15 = <WrapItem alignItems="center" color="colorPalette.fg/70" display="inline-flex" gap={1}>{t11}{t13}{t14}</WrapItem>;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = !!text;
  let t17;
  let t18;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setShowFullMarkdown(false);
    t18 = () => setShowFullMarkdown(true);
    $[21] = t17;
    $[22] = t18;
  } else {
    t17 = $[21];
    t18 = $[22];
  }
  let t19;
  if ($[23] !== canViewAll || $[24] !== compact || $[25] !== headlines || $[26] !== isError || $[27] !== isStreaming || $[28] !== showFullMarkdown || $[29] !== t16) {
    t19 = <ThinkingBadgesInline canViewAll={canViewAll} compact={compact} hasText={t16} headlines={headlines} isError={isError} isStreaming={isStreaming} showFullMarkdown={showFullMarkdown} onShowHighlights={t17} onViewAll={t18} />;
    $[23] = canViewAll;
    $[24] = compact;
    $[25] = headlines;
    $[26] = isError;
    $[27] = isStreaming;
    $[28] = showFullMarkdown;
    $[29] = t16;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t15 || $[32] !== t19) {
    t20 = <Wrap align="center" gap={1.5} py={0.5}>{t15}{t19}</Wrap>;
    $[31] = t15;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== compact || $[35] !== displayText || $[36] !== showFullMarkdown) {
    t21 = showFullMarkdown ? <Box ref={fullScrollRef} bg="colorPalette.emphasized/10" borderColor="colorPalette.emphasized/30" borderRadius="md" borderWidth="1px" color="colorPalette.fg/65" marginTop={2} maxHeight="16em" overflowY="auto" padding={2}><MarkdownRenderer baseFontSize={compact ? "0.75em" : "0.8em"} baseTextColor="colorPalette.fg/65" injectParagraphsIfBreaksNotPresent={true} preset="chat-message">{displayText}</MarkdownRenderer></Box> : null;
    $[34] = compact;
    $[35] = displayText;
    $[36] = showFullMarkdown;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t10 || $[39] !== t20 || $[40] !== t21) {
    t22 = <Box marginBottom={t10}>{t20}{t21}</Box>;
    $[38] = t10;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  return t22;
};
function ThinkingBadgesInline(props: {
  headlines: string[];
  isStreaming: boolean;
  isError: boolean;
  hasText: boolean;
  compact?: boolean;
  canViewAll: boolean;
  showFullMarkdown: boolean;
  onViewAll: () => void;
  onShowHighlights: () => void;
}) {
  const {
    headlines,
    isStreaming,
    isError,
    hasText,
    compact,
    canViewAll,
    showFullMarkdown,
    onViewAll,
    onShowHighlights
  } = props;
  const seenKeysRef = useRef<Set<string>>(new Set());
  const [popKeys, setPopKeys] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!hasText && isStreaming) {
      seenKeysRef.current = new Set();
      setPopKeys(new Set());
      return;
    }
    const fresh = new Set<string>();
    for (const h of headlines) {
      const key = h.toLowerCase();
      if (!seenKeysRef.current.has(key)) {
        seenKeysRef.current.add(key);
        fresh.add(key);
      }
    }
    if (fresh.size > 0) {
      setPopKeys(fresh);
    }
  }, [headlines, hasText, isStreaming]);
  let popOrder = 0;
  return <>
      {headlines.map(headline => {
      const key_0 = headline.toLowerCase();
      const shouldPop = popKeys.has(key_0);
      const delayMs = shouldPop ? Math.min(popOrder++, 8) * 40 : 0;
      return <WrapItem key={key_0}>
            <Badge animation={shouldPop ? `${badgePopIn} 320ms cubic-bezier(0.22, 1, 0.36, 1) both` : undefined} fontSize={compact ? '0.7em' : '0.75em'} overflow={'hidden'} px={2} py={0.5} size={compact ? 'sm' : 'md'} style={shouldPop ? {
          animationDelay: `${delayMs}ms`
        } : undefined} textOverflow={'ellipsis'} title={headline} variant={'outline'} whiteSpace={'nowrap'}>
              {headline}
            </Badge>
          </WrapItem>;
    })}

      {headlines.length === 0 && isStreaming ? <WrapItem>
          <Text color={'colorPalette.fg/50'} fontSize={compact ? 'xs' : 'sm'} fontStyle={'italic'}>
            …
          </Text>
        </WrapItem> : null}

      {headlines.length === 0 && !isStreaming && isError ? <WrapItem>
          <Text color={'colorPalette.fg/50'} fontSize={compact ? 'xs' : 'sm'}>
            Thinking failed.
          </Text>
        </WrapItem> : null}

      {canViewAll ? <WrapItem>
          <chakra.button _hover={{
        bg: 'colorPalette.emphasized/20'
      }} alignItems={'center'} bg={showFullMarkdown ? 'colorPalette.emphasized/20' : 'transparent'} borderColor={'colorPalette.emphasized/40'} borderRadius={'l2'} borderWidth={'1px'} color={'colorPalette.fg/70'} cursor={'pointer'} display={'inline-flex'} fontSize={compact ? '0.5em' : '0.5em'} fontWeight={'semibold'} lineHeight={'1.3'} px={2} py={0.5} type={'button'} onClick={e => {
        e.stopPropagation();
        if (showFullMarkdown) {
          onShowHighlights();
        } else {
          onViewAll();
        }
      }}>
            {showFullMarkdown ? '-' : '+'}
          </chakra.button>
        </WrapItem> : null}
    </>;
}

/** GraphQL selection for ScoComment.thinking — keep query + subscription in sync. */
export const COMMENT_THINKING_SELECTION = `
  thinking {
    status
    text
    startedAt
    completedAt
    truncated
  }
`;